/**
 * `/host` 호스트 콘솔 — §14.
 *
 *   app-shell · 자막 피드(좌) + 코치 패널(우)
 *   badge(LIVE·확인필요·따라잡는중)
 *   button-group(방향⇄·복사·저장·요약·지우기·기록·의도해석·설정)
 *   drawer(설정) · alert(적체·model_down) · confirm-dialog(방 종료)
 *   + §4 마이크 캡처
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { AppShell } from '@cupia/ui/app-shell'
import { ButtonGroup } from '@cupia/ui/button-group'
import { Button } from '@cupia/ui/button'
import { Badge } from '@cupia/ui/badge'
import { Alert, AlertDescription } from '@cupia/ui/alert'
import { confirm } from '@cupia/ui/confirm-dialog'
import {
  ArrowLeftRight,
  Brain,
  Check,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  Eraser,
  Eye,
  EyeOff,
  FileText,
  Mic,
  MicOff,
  Save,
  Settings,
  Share2,
  Radio,
  Signal,
} from 'lucide-react'
import { Brand } from '../components/brand'
import { CaptionFeed } from '../components/caption-feed'
import { SplitFeed, useSplitRowsFallback } from '../components/split-feed'
import { SplitRows } from '../components/split-rows'
import { ChatPanel } from '../components/chat-panel'
import { CoachDock } from '../components/coach-dock'
import { AssistPanel } from '../components/assist-panel'
import { StatusAlerts } from '../components/status-alerts'
import { LagBadge, ListeningBadge, LiveBadge } from '../components/badges'
import { LevelMeter } from '../components/level-meter'
import { ThemeControls } from '../components/theme-controls'
import { TranscriptDownload } from '../components/transcript-download'
import { HeaderToolsToggle } from '../components/header-tools-toggle'
import { useHeaderTools } from '../lib/use-header-tools'
import { SelfAdminLink } from '../components/admin-link'
import { SettingsDrawer, type HostSettings } from '../components/settings-drawer'
import {
  SummaryDrawer,
  kindLabel,
  type SummaryEntry,
} from '../components/summary-drawer'
import { useRoomStream } from '../lib/use-room-stream'
import { markCleared, visibleFeed, type ClearMark } from '../lib/clear-view'
import { buildTranscript } from '../lib/transcript'
import { useFeedOrder } from '../lib/use-feed-order'
import { useSplitView } from '../lib/use-split-view'
import { useSourceVeil } from '../lib/use-source-veil'
import {
  acquireWakeLock,
  releaseWakeLock,
  setWakeLockWanted,
  subscribeWakeLockStatus,
  useWakeLockPref,
  watchVisibilityReacquire,
  type WakeLockStatus,
} from '../lib/wake-lock'
import { useCollapseInferred } from '../lib/use-collapse-inferred'
import {
  hasAssistantJudgeItems,
  useCollapseAssistant,
} from '../lib/use-collapse-assistant'
import { useCaptionScale } from '../lib/use-caption-scale'
import { useCoachCollapse } from '../lib/use-coach-collapse'
import { PRESENTATION_STEP } from '../lib/caption-scale'
import { useServerConfig } from '../lib/use-server-config'
import { clearCredential, loadCredential } from '../lib/session'
import { createRoomAuth, type RoomAuth } from '../lib/token'
import {
  ApiError,
  closeRoom as closeRoomApi,
  deleteCaption,
  deleteChat,
  fetchRoomSettings,
  requestAnalyze,
  requestAssist,
  setDirection,
  setRecord,
} from '../lib/api'
import { buildEntryInfo, copyText, directionLabel, languageName } from '../lib/room-info'
import { isBidirectionalPair, LANDING_TARGETS, REVERSE_TARGET_CODES } from '../lib/languages'
import { USE_MOCK } from '../lib/config'
import { MicCapture, captureStatusNote, type CaptureStats } from '../lib/capture'
import { classifyMicError } from '../lib/mic-error'
import { SILENCE_DBFS } from '../lib/resampler'
import { useNoiseFloor } from '../lib/use-noise-floor'
import { FRAME_MS } from '../lib/framer'
import type { ChatItem, FinalItem } from '../lib/reducer'

export function HostRoute(): React.ReactElement {
  const navigate = useNavigate()
  // host 칸만 본다 — 같은 탭에서 뷰어 링크를 열어도 이 자격은 덮이지
  // 않는다(lib/session.ts, TASK_sec_fix_f #2).
  const cred = useMemo(() => loadCredential(undefined, 'host'), [])
  // §5.2 방 자격 수명주기 — 토큰(갱신은 /api/rooms/{room}/token). identity
  // 를 고정해야 useRoomStream 이 렌더마다 재접속하지 않는다.
  const auth = useMemo(
    () => (cred && cred.role === 'host' ? createRoomAuth(cred) : null),
    [cred],
  )

  useEffect(() => {
    if (!cred || cred.role !== 'host') void navigate('/')
  }, [cred, navigate])

  if (!cred || cred.role !== 'host' || !auth) {
    return (
      <div className="flex min-h-screen items-center justify-center text-[var(--color-muted-foreground)]">
        호스트 자격이 없습니다. 랜딩으로 이동합니다…
      </div>
    )
  }

  return (
    <HostConsole
      room={cred.room}
      hostKey={cred.key}
      auth={auth}
      passcode={cred.passcode}
      viewKey={cred.viewKey}
      publicEntry={cred.publicEntry ?? false}
      initialSrc={cred.src}
      initialDst={cred.dst}
      initialBidi={cred.bidirectional ?? false}
      initialName={cred.name ?? ''}
      initialTopic={cred.topic ?? ''}
    />
  )
}

function HostConsole({
  room,
  hostKey,
  auth,
  passcode,
  viewKey,
  publicEntry,
  initialSrc,
  initialDst,
  initialBidi,
  initialName,
  initialTopic,
}: {
  room: string
  hostKey: string
  /** §5.2 방 자격 관리자 — REST·WS 는 전부 auth.current() 를 쓴다 */
  auth: RoomAuth
  /** 6자리 입장 패스코드 — 구두 공유용. URL 에는 싣지 않는다(§10.0) */
  passcode?: string | undefined
  /** 공개 방 직통 링크(#k=)용 — 비공개 방에서는 쓰지 않는다(§10.2) */
  viewKey?: string | undefined
  publicEntry: boolean
  initialSrc: string
  initialDst: string
  /** 양방향 통역 여부(§ bidi) — 자격에서 온 자리표, 서버 설정이 덮어쓴다 */
  initialBidi: boolean
  /** S28 회의 이름 — 자격에서 온 자리표, 서버 설정이 덮어쓴다 */
  initialName: string
  /** S28 회의 주제 — 자격에서 온 자리표, 서버 설정이 덮어쓴다 */
  initialTopic: string
}): React.ReactElement {
  const navigate = useNavigate()
  const { state, conn, sink, loadMore, markClientGap, sendChat,
          vadFloorDbfs, linkStats } = useRoomStream({
    room,
    key: hostKey,
    role: 'host',
    auth,
  })
  const scale = useCaptionScale()
  const presentation = scale.step >= PRESENTATION_STEP
  // 코치 패널 접기 — 사용자별 저장, 좁은 화면은 기본 접힘 + 덮기(overlay).
  const {
    collapsed: coachCollapsed,
    narrow: coachNarrow,
    toggle: toggleCoach,
  } = useCoachCollapse()
  // 호스트의 정렬 취향은 뷰어와 **독립**이다(같은 사람이 두 역할을 오간다).
  const { order, setOrder } = useFeedOrder('host')
  // 분할 보기(D1) — 정렬과 독립, 뷰어·호스트 각자 기억한다.
  const { mode: splitMode, setMode: setSplitMode } = useSplitView('host')
  /** D3-4 — `rows` 는 ≤640px 에서 v4 탭 계약으로 폴백한다(상태 변경 아님). */
  const splitRowsFallback = useSplitRowsFallback()
  // 원문 가림 — 이 브라우저의 취향(시각만, lib/source-veil.ts). 헤더 버튼과
  // 설정 서랍 스위치가 **이 하나의 상태**를 같이 본다.
  const veil = useSourceVeil('host')
  // 화면 켜짐 유지 — 이 브라우저의 취향(lib/wake-lock.ts). 마이크 시작
  // 직후에 요청하고, 중지·이탈 시 놓는다.
  const wakeLockPref = useWakeLockPref()
  const [wakeLockStatus, setWakeLockStatus] = useState<WakeLockStatus>('released')
  // §6.5-7 유령(추론) 자막 접기 — 원문 가림과 다른 축이라 별도 저장 키를 쓴다.
  const collapseInferred = useCollapseInferred()
  // 판정기 자막 접기 — 추론 접기와 **별도 축**(저장 키도 다르다). 기본 off.
  const collapseAssistant = useCollapseAssistant()
  const serverConfig = useServerConfig()
  // 휴대폰 머리글 도구줄 접기/펼치기 — 뷰어와 독립 저장(lib/use-header-tools.ts).
  const tools = useHeaderTools('host')
  /**
   * 방이 사라졌다 — REST 동작도 전부 실패한다(§9). 버튼을 살려 두면 누를
   * 때마다 "의도 해석 요청이 실패했습니다" 같은 날것의 토스트만 나온다.
   * 원인은 요청이 아니라 방이 없다는 것이므로, 그 사실을 배너로 말하고
   * 조작은 막는다.
   */
  // terminated(자격 종결 — §5.2 재교환 실패)도 같은 취급이다: REST 도 같은
  // 자격을 쓰므로 버튼을 살려 두면 날것의 실패 토스트만 반복된다.
  // room_closed(종료 증거)도 같다 — 방이 없으니 REST 도 전부 실패한다.
  const roomGone = conn === 'room_gone' || conn === 'room_closed'
    || conn === 'terminated'

  const [src, setSrc] = useState(initialSrc)
  const [dst, setDst] = useState(initialDst)
  // §bidi 양방향 여부 — src/dst 와 한 묶음의 방 상태다. 서버 설정 로드가
  // 아래에서 셋을 함께 덮어쓴다(새로고침 복원의 단일 출처).
  const [bidi, setBidi] = useState(initialBidi)
  // S28 회의 이름·주제 — src/dst/bidi 와 같은 묶음: 서버 설정 로드가
  // 아래에서 함께 덮어쓴다(새로고침 복원의 단일 출처).
  const [name, setName] = useState(initialName)
  const [topic, setTopic] = useState(initialTopic)
  const [settingsOpen, setSettingsOpen] = useState(false)
  // 요약·의도 해석은 코치 피드가 아니라 **자기 표면**에서 산다(§14.2).
  // 회의 동안 받은 결과를 모두 남겨 다시 열 수 있게 한다 — 재실행은 모델
  // 호출 비용이고, 호스트는 앞서 받은 요약을 다시 보려 한다.
  const [summaryOpen, setSummaryOpen] = useState(false)
  const [summaries, setSummaries] = useState<SummaryEntry[]>([])
  const [summarySel, setSummarySel] = useState<string | undefined>()
  const [summaryPending, setSummaryPending] = useState<
    'summary' | 'intent' | null
  >(null)
  const [settings, setSettings] = useState<HostSettings>({
    // 화면 표시 취향(coach)은 로컬 상태로 충분하다 — 방이 아니라
    // 이 브라우저에 속한 값이다. (원문 가림은 useSourceVeil 이 든다.)
    coach: true,
    // 아래 둘은 **방의 상태**다. 여기 값은 서버 응답이 도착하기 전의
    // 자리표일 뿐이고, 아래 useEffect 가 서버 진실로 덮어쓴다.
    record: false,
    retentionDays: 30,
    recordLocked: false,
    recordAvailable: false,
  })

  /**
   * 지우기(§14) — **표시 수위표**(lib/clear-view.ts). 리듀서 상태는 그대로
   * 두고 렌더만 거른다: 복사·저장(transcript)은 계속 전체를 읽고, 참가자
   * 화면·서버는 영향이 없다. 새로고침하면 서버 재생으로 되돌아온다.
   */
  const [clearMark, setClearMark] = useState<ClearMark | null>(null)

  const [micOn, setMicOn] = useState(false)
  const [muted, setMuted] = useState(false)
  const [micError, setMicError] = useState<string | null>(null)
  /**
   * §4 캡처 생존 — 마이크가 죽었거나 오디오가 서버에 안 가고 있는 상태.
   *
   * 이게 없으면 화면이 **살아 있는 것처럼 보이는데 실제로는 죽어 있다**:
   * 마이크 버튼은 켜져 있고 레벨 미터는 마지막 값에 얼어붙은 채 남아,
   * 호스트는 자기 말이 통역되고 있다고 믿는다(실제 사고 경로다 —
   * 재접속 후 캡처가 다시 무장되지 않아 무음만 흘렀다).
   */
  const [micStale, setMicStale] = useState<string | null>(null)
  const [stats, setStats] = useState<CaptureStats | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  const [entryCopied, setEntryCopied] = useState(false)
  const captureRef = useRef<MicCapture | null>(null)

  const notify = useCallback((message: string) => {
    setToast(message)
    setTimeout(() => setToast(null), 2600)
  }, [])
  // 위쪽 effect 가 notify 를 쓰되 의존성으로 잡지 않도록(방 설정 로드는
  // room/hostKey 가 바뀔 때만 다시 돌아야 한다) ref 로 건넨다.
  const notifyRef = useRef(notify)
  notifyRef.current = notify

  /**
   * 방의 서버 진실을 읽어 온다 — **새로고침 복원의 단일 출처**.
   *
   * 이게 없으면 F5 한 번에 화면이 클라이언트 기본값으로 되돌아간다: 기록
   * ON 인 방이 OFF 로 보이고(실제 발생), 전환해 둔 통역 방향도 원래대로
   * 표시된다. 방에 속한 값은 방에서 읽는다.
   */
  useEffect(() => {
    let cancelled = false
    if (USE_MOCK) return
    void fetchRoomSettings(room, auth.current())
      .then((s) => {
        if (cancelled) return
        setSrc(s.src)
        setDst(s.dst)
        // §bidi 구 서버는 이 필드를 안 준다(undefined) → 단방향으로 복원.
        setBidi(s.bidirectional ?? false)
        // S28 구 서버는 이 필드를 안 준다(undefined) → 자리표를 유지한다.
        if (s.name !== undefined) setName(s.name)
        if (s.topic !== undefined) setTopic(s.topic)
        setSettings((prev) => ({
          ...prev,
          record: s.record,
          retentionDays: (s.retention_days as 7 | 30 | 90) ?? prev.retentionDays,
          recordLocked: s.record_locked,
          recordAvailable: s.record_available ?? true,
          // §7.2 — 코치는 **방의 상태**다(로컬 표시 취향이 아니다).
          // 예전에는 `coach: true` 로 하드코딩해 놓고 방이 실제로는
          // coach=false 인 것을 무시했다: 화면은 패널을 그리는데 서버는
          // 코치를 한 번도 돌리지 않는, "켜졌다고 거짓말하는 UI" 였다.
          // 구 서버(필드 없음) 호환을 위해 undefined 면 이전 값을 둔다.
          coach: s.coach ?? prev.coach,
        }))
      })
      .catch(() => {
        // 실패해도 콘솔은 살아 있어야 한다(자막 중계가 본업이다).
        // 다만 방 설정 표시는 신뢰할 수 없으므로 조용히 두지 않는다.
        if (!cancelled) notifyRef.current('방 설정을 불러오지 못했습니다.')
      })
    return () => {
      cancelled = true
    }
  }, [room, auth])

  // S28 — 방 이름이 있으면 탭 제목에 반영한다(여러 회의를 동시에 열어 둔
  // 호스트가 탭으로 구분할 수 있게).
  useEffect(() => {
    if (name) document.title = `${name} — omnitrans`
  }, [name])

  /* ── §4 마이크 캡처 ────────────────────────────────────────────────── */

  const startMic = useCallback(async () => {
    setMicError(null)
    setMicStale(null)
    try {
      const capture = new MicCapture({
        sink,
        onStats: setStats,
        // §4 생존 신호 — 죽으면 화면이 말하고, 살아나면 스스로 지운다.
        // 복구는 캡처가 알아서 하므로 사용자가 누를 것은 없다(문구도 그렇게
        // 말한다). 이유 문자열은 로그용이고 화면에는 사람이 읽을 문장을 쓴다.
        onLiveness: (alive) => {
          setMicStale(alive
            ? null
            : '마이크 입력이 끊겼습니다 — 자동으로 재연결 중입니다.')
        },
        onGap: (gap) => {
          // 두 사유(적체 드롭 / 단절 보류 큐 축출) 모두 유실 구간을 화면에
          // 명시한다(§4 gap 마커). from/to 는 **실제 시각**이다: 드롭한
          // 프레임 수 × 프레임 길이만큼 거슬러 올라간 구간이 곧 유실 구간이다.
          const now = Date.now()
          markClientGap(now - gap.frames * FRAME_MS, now, gap.frames, gap.reason)
        },
      })
      await capture.start()
      captureRef.current = capture
      setMicOn(true)
      setStats(capture.stats)
      if (wakeLockPref.enabled) {
        setWakeLockWanted(true)
        void acquireWakeLock().then(setWakeLockStatus)
      }
    } catch (err) {
      console.error('[mic]', err)
      setMicError(classifyMicError(err))
    }
  }, [sink, markClientGap, wakeLockPref.enabled])

  const stopMic = useCallback(async () => {
    await captureRef.current?.stop()
    captureRef.current = null
    setMicOn(false)
    setMuted(false)
    setMicStale(null)
    setWakeLockWanted(false)
    void releaseWakeLock()
  }, [])

  // §4 "지금 오디오가 서버에 가고 있는가" 를 한 문장으로(순수 함수 — 테스트는
  // capture.test.ts 가 한다. 라우트는 mic/WS 의존 때문에 jsdom 에서 못 띄운다).
  const micStatusNote = captureStatusNote({ stale: micStale, stats })

  // D4 — 주변 잡음 바닥 안내. `captureStatusNote` **체인에 합치지 않는다**:
  // 그 함수는 "마이크 고장 → 보류" 우선순위로 하나만 반환하므로, 잡음
  // 안내를 넣으면 고장 안내를 가리거나(치명) 영영 안 보인다. 잡음은 고장이
  // 아니라 **환경**이라 다른 축이고, 그래서 별도 줄이다.
  // 표본 선택은 에너지 상한(-35)이 하므로 서버 VAD 바닥은 이 축에 필요
  // 없다(F3 — zone 으로 뽑으면 기본 바닥 -55 에서 경고가 도달 불가였다).
  // `framesGenerated` 는 캡처가 **만든** 프레임의 단조 카운터다 — 이 값이
  // 변할 때만 새 표본이다(F4). `sent + dropped` 를 쓰면 단절 보류(stalled)
  // 프레임이 어느 축에도 안 들어가 최대 8 s 의 활성 입력이 빠진다(R2).
  const noise = useNoiseFloor(
    stats ? stats.dbfs : SILENCE_DBFS,
    micOn && !muted,
    stats ? stats.framesGenerated : 0,
  )

  // 화면 켜짐 유지 취향을 회의 중에 바꾸면 즉시 반영한다 — 마이크가 이미
  // 켜져 있는데 설정만 바뀐 경우를 놓치지 않는다.
  useEffect(() => {
    // 연결이 끝난 방에서는 선호를 다시 켜도 잠금을 집지 않는다 — 게이트는
    // "참여 중 ∧ 선호 on" 이고, roomGone 이면 참여 중이 아니다(P2).
    if (!micOn || roomGone) return
    if (wakeLockPref.enabled) {
      setWakeLockWanted(true)
      void acquireWakeLock().then(setWakeLockStatus)
    } else {
      setWakeLockWanted(false)
      void releaseWakeLock()
    }
  }, [wakeLockPref.enabled, micOn, roomGone])

  // 탭이 다시 보이면(백그라운드에서 복귀) 원하는 경우 잠금을 재요청한다.
  useEffect(() => watchVisibilityReacquire(), [])

  // 브라우저發 release(예: OS 정책)를 상태에 반영한다(CORRECTION #1 SHOULD 4).
  useEffect(() => subscribeWakeLockStatus(setWakeLockStatus), [])

  // 연결이 끝난 방(종료·강제 퇴장·재연결 실패)에서는 화면이 계속 켜져
  // 있을 이유가 없다(CORRECTION #1 BLOCK 2) — 마이크가 켜져 있어도 놓는다.
  useEffect(() => {
    if (!roomGone) return
    setWakeLockWanted(false)
    void releaseWakeLock()
  }, [roomGone])

  useEffect(() => {
    return () => {
      void captureRef.current?.stop()
      setWakeLockWanted(false)
      void releaseWakeLock()
    }
  }, [])

  const toggleMute = useCallback(() => {
    const next = !muted
    setMuted(next)
    captureRef.current?.setMuted(next) // 트랙 disable — 서버 도달 전 차단(§4)
  }, [muted])

  /* ── 버튼 그룹 동작 ────────────────────────────────────────────────── */

  /**
   * §bidi 통역 방향 3-상태 전환 — vi→ko / ko→vi / 양방향(ko↔vi).
   *
   * 스왑 버튼(src↔dst) 하나로는 "쌍 안에서 어느 쪽으로 말해도 통역" 이라는
   * 세 번째 상태를 표현할 수 없어, 세 상태를 고르는 세그먼트 컨트롤로 바꿨다.
   * 서버는 양방향을 두 언어가 모두 유효 타깃인 쌍({ko, vi})에서만 허용하므로
   * 양방향은 src=vi/dst=ko 로 고정해 보낸다(순서는 표시에만 영향).
   *
   * 방향 전환은 §5 리셋 조건이다 — **서버가** 진행 중 발화를 flush 하고
   * 후검증 이력을 비운다. 화면 상태만 바꾸면 서버는 여전히 옛 방향으로
   * 통역한다(그리고 타깃어 발화를 전부 skip 으로 버린다).
   */
  const applyDirection = useCallback(
    (nextSrc: string, nextDst: string, nextBidi: boolean) => {
      // 이미 그 상태면 서버 왕복·flush 를 아낀다(중복 클릭은 무동작).
      if (nextSrc === src && nextDst === dst && nextBidi === bidi) return
      const prevSrc = src
      const prevDst = dst
      const prevBidi = bidi
      setSrc(nextSrc)
      setDst(nextDst)
      setBidi(nextBidi)
      const label = nextBidi
        ? `양방향 (${directionLabel(nextSrc, nextDst, { compact: true, bidirectional: true })})`
        : directionLabel(nextSrc, nextDst)
      void setDirection(room, auth.current(), nextSrc, nextDst, nextBidi)
        .then(() => notify(`통역 방향: ${label}`))
        .catch(() => {
          // 실패하면 이전 상태로 되돌린다 — 서버와 어긋난 표시가 더 나쁘다.
          setSrc(prevSrc)
          setDst(prevDst)
          setBidi(prevBidi)
          notify('방향 전환에 실패했습니다.')
        })
    },
    [src, dst, bidi, room, auth, notify],
  )

  /**
   * §bidi 방향 컨트롤 옵션 — **방의 실제 언어**에서 유도한다(S36).
   *
   * 예전엔 이 배열이 `vi`/`ko` 리터럴로 하드코딩돼 있었다: 프랑스어로 방을
   * 열어도 화면엔 `vi→ko`/`ko→vi`/`양방향` 이 뜨고, 그 버튼을 누르면
   * 서버가 방을 통째로 한↔베로 바꿔 버렸다(실측 재현, §0). 서버 쪽 가드는
   * 별도 태스크(S-DIR)가 넣지만, UI 도 애초에 방 밖의 선택지를 보여주지
   * 않아야 그 손상 경로가 클릭 한 번으로 열리지 않는다.
   *
   * - ko/vi 쌍: 오늘과 바이트 단위로 같은 3-상태
   *   (`direction-vi-ko`/`direction-ko-vi`/`direction-bidi`). 이 가지는
   *   이 과제(bidi_asym)에서 손대지 않는다 — 버튼 키·라벨 무변경.
   * - 그 밖에 `isBidirectionalPair(src, dst)` 가 참인 소스(fr 등,
   *   bidi_asym D1(b)): 단방향 하나(`direction-{src}-ko`)에 더해
   *   양방향 버튼 하나를 낸다. "양방향" 이라 쓰되 비대칭이므로(한국어
   *   발화만 src 로 역번역, 그 밖은 그대로 dst 로) 툴팁에 그 사실을 적는다.
   * - 그 외(비-타깃 소스, 역번역 축 없음): 역방향·양방향 버튼 없이
   *   `LANDING_TARGETS[0]`(ko) 로의 전환 버튼 하나(`direction-{src}-ko`).
   * - src === dst(개설 시 검증이 못 잡은 동일 언어 방): 같은 언어 항목은
   *   `disabled`. 서버에 새 거부는 이번에 넣지 않는다(별도 판단).
   *
   * 2026-09-22 LANDING_TARGETS(ko 단일 대상) 결정: 외국어→외국어 전환
   * (fr→vi 류)은 제품 컨셉과 안 맞아 제거했다. 여기서 쓰는
   * `primaryTargetCode` 가 그 결정의 단일 출처다(TASK_dir_options_ko.md
   * CORRECTION #1).
   */
  const primaryTargetCode = LANDING_TARGETS[0]
  type DirectionOption = { key: string; src: string; dst: string; bidi: boolean;
    disabled: boolean; label: string; tooltip?: string }
  const directionOptions = useMemo((): DirectionOption[] => {
    if (src !== dst && (src === 'vi' || src === 'ko') && (dst === 'vi' || dst === 'ko')) {
      return [
        { key: 'vi-ko', src: 'vi', dst: 'ko', bidi: false, disabled: false, label: '베트남어 → 한국어' },
        { key: 'ko-vi', src: 'ko', dst: 'vi', bidi: false, disabled: false, label: '한국어 → 베트남어' },
        // 양방향은 **회의 중 여기서** 켠다(2026-09-07 사용자 결정 — 개설 화면의
        // 스위치는 제거). 서버 set_direction 이 is_bidirectional_pair 로 재검증한다.
        // 양방향은 src=vi/dst=ko 로 고정해 보낸다(순서는 표시에만 영향).
        { key: 'bidi', src: 'vi', dst: 'ko', bidi: true, disabled: false,
          label: '양방향 (ko ↔ vi)',
          tooltip: '한국어 발화는 베트남어로, 베트남어 발화는 한국어로 통역합니다.' },
      ]
    }
    if (REVERSE_TARGET_CODES.includes(src) && isBidirectionalPair(src, dst)) {
      // 비대칭 양방향(bidi_asym D1(b)) — fr→ko 방에서 나오는 두 버튼:
      // fr→ko(단방향) · 양방향(fr↔ko).
      return [
        // 단방향 옵션은 LANDING_TARGETS[0](ko) 하나로만 낸다 — fr→vi 처럼
        // ko 가 아닌 타깃으로의 전환은 LANDING_TARGETS 결정(2026-09-22)으로
        // 제거했다(구 agy H2 근거는 이 결정으로 대체).
        { key: `${src}-${primaryTargetCode}`, src, dst: primaryTargetCode, bidi: false,
          disabled: false, label: directionLabel(src, primaryTargetCode) },
        // 단방향 역전(dst→src)은 **없다** — 서버가 비-타깃 src 방의 src 전환을
        // `direction_outside_room_pair` 로 거부한다(main.py set_direction). 역번역은
        // 양방향 모드 안에서만 존재하는 경로다(codex 교차리뷰 #1, 2026-09-08).
        { key: 'bidi', src, dst, bidi: true, disabled: !isBidirectionalPair(src, dst),
          label: `양방향 (${directionLabel(src, dst, { compact: true, bidirectional: true })})`,
          tooltip: `${languageName(dst)} 발화는 ${languageName(src)}로, 그 외는 ${languageName(dst)}로 통역합니다.` },
      ]
    }
    // 비-타깃 소스(역번역 축 없음) — 또는 src===dst 인 열린 방(개설 검증이
    // 못 잡은 경우). 역방향·양방향 버튼은 아예 만들지 않는다 — 서버가
    // 거부할 조합을 화면에 안 보여야 손상 경로가 안 열린다. 타깃은
    // LANDING_TARGETS[0](ko) 하나뿐이다(2026-09-22 결정).
    return [{
      key: `${src}-${primaryTargetCode}`,
      src,
      dst: primaryTargetCode,
      bidi: false,
      disabled: primaryTargetCode === src,
      label: directionLabel(src, primaryTargetCode),
    }]
  }, [src, dst, bidi])

  /**
   * 렌더용 피드 — 지우기 수위표를 적용한 목록. **transcript 는 이걸 쓰지
   * 않는다**: 복사·저장은 지운 뒤에도 전체 기록을 담아야 한다.
   * (identity 를 memo 로 고정해야 CaptionFeed 의 앵커링이 렌더마다 돌지 않는다.)
   */
  const feedItems = useMemo(
    () => visibleFeed(state.items, clearMark),
    [state.items, clearMark],
  )
  /**
   * 판정기 배지가 이 방에서 실제로 도는가 — 접기 손잡이를 세울지의 판정(T3).
   * `state.items` 전체를 본다(화면에서 지운 뒤에도 방의 성질은 그대로다).
   */
  const judgeBadgeLive = useMemo(
    () => hasAssistantJudgeItems(state.items),
    [state.items],
  )

  // 문단이 있으면 본문=통합 문단 / 부록=원본 카드, 없으면 오늘 형식
  // 그대로다(lib/transcript.ts — 형식·근거는 그 파일이 정본).
  const transcript = useMemo(() => buildTranscript(state.items), [state.items])

  /**
   * 입장 정보 복사 — 참가자에게 그대로 붙여넣을 초대 문구(§10.0.1).
   *
   * 비공개 방(기본)은 **주소와 패스코드를 분리**해 적는다. 링크에 패스코드를
   * 넣으면 그 순간 두 번째 인증 요소가 브라우저 히스토리·프록시 로그·메신저
   * 미리보기 서버로 새어 2요소가 1요소로 무너진다. 공개 방만 직통 링크
   * (fragment 키)를 쓴다 — fragment 는 서버로 전송되지 않는 설계된 경로다.
   */
  const copyEntryInfo = useCallback(() => {
    const text = buildEntryInfo({
      room,
      passcode,
      publicEntry,
      viewKey,
      origin: location.origin,
      name,
      topic,
    })
    void copyText(text).then((ok) => {
      setEntryCopied(ok)
      notify(ok ? '입장 정보를 복사했습니다.' : '복사에 실패했습니다.')
      if (ok) setTimeout(() => setEntryCopied(false), 2400)
    })
  }, [room, passcode, publicEntry, viewKey, name, topic, notify])

  const copyAll = useCallback(() => {
    void navigator.clipboard
      .writeText(transcript)
      .then(() => notify('자막을 클립보드에 복사했습니다.'))
      .catch(() => notify('복사에 실패했습니다.'))
  }, [transcript, notify])

  const saveAll = useCallback(() => {
    const blob = new Blob([transcript], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `omnitrans-${room}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.txt`
    a.click()
    URL.revokeObjectURL(url)
    notify('자막을 파일로 저장했습니다.')
  }, [transcript, room, notify])

  const requestSummary = useCallback(
    (kind: 'summary' | 'intent') => {
      const label = kindLabel(kind)
      // 전용 표면을 **먼저** 연다: 진행·실패가 그 안에서 보여야 한다.
      // 예전엔 실패가 날것의 토스트로만 떠서 무엇이 실패했는지 남지 않았다.
      setSummaryPending(kind)
      setSummaryOpen(true)
      const fail = (msg: string) => {
        setSummaryPending(null)
        const id = `err-${Date.now()}`
        setSummaries((prev) => [{ id, kind, body: '', ts: Date.now(), error: msg }, ...prev])
        setSummarySel(id)
      }
      if (USE_MOCK) {
        void import('../mock/client').then((m) => m.mockSummary(room, kind))
        return
      }
      // 결과는 REST 응답이 아니라 `summary` 이벤트로 소켓에 온다(§8.2).
      void requestAnalyze(room, auth.current(), kind).catch((err: unknown) =>
        fail(
          err instanceof ApiError && err.status === 429
            ? '요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.'
            : `${label} 요청이 실패했습니다.`,
        ),
      )
    },
    [room, auth],
  )

  /* ── 어시스트 질의(DEF-071) ─────────────────────────────────────────
   *
   * 응답은 REST 가 아니라 `assist` 이벤트로 소켓에 온다(요약과 같은 축).
   * 여기서 하는 일은 **발사와 거절 표면화**뿐이다.
   *
   * 거절을 삼키지 않는다: 429/503 을 조용히 두면 호스트는 답이 오는 줄
   * 알고 기다린다(§1 조용한 실패 금지). 패널이 문장을 보여 줄 수 있도록
   * throw 로 되돌린다.
   */
  const [assistPending, setAssistPending] = useState(false)
  const askAssist = useCallback(
    async (q: string): Promise<void> => {
      if (!auth) return
      setAssistPending(true)
      try {
        await requestAssist(room, auth.current(), q)
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 429) {
          throw new Error(
            err.code === 'assist_busy'
              ? '이전 질문에 아직 답하는 중입니다. 잠시 후 다시 시도해 주세요.'
              : '요청이 너무 잦습니다. 잠시 후 다시 시도해 주세요.',
          )
        }
        if (err instanceof ApiError && err.status === 503) {
          throw new Error('통역이 밀려 있어 잠시 뒤에 답할 수 있습니다.')
        }
        throw new Error('질문 요청이 실패했습니다.')
      } finally {
        setAssistPending(false)
      }
    },
    [room, auth],
  )

  // `summary` 이벤트가 오면 목록에 쌓고 그 항목을 보여준다.
  // 리듀서의 `state.summary` 는 **최신 1건**만 들고 있으므로, 여기서 이력을
  // 만든다(리듀서를 건드리면 뷰어·재생 경로까지 영향이 간다).
  useEffect(() => {
    const s = state.summary
    if (!s) return
    setSummaryPending(null)
    setSummaries((prev) => {
      if (prev.some((e) => e.ts === s.ts && e.kind === s.kind)) return prev
      const id = `s-${s.ts}-${s.kind}`
      setSummarySel(id)
      return [{ id, kind: s.kind, body: s.body, ts: s.ts }, ...prev]
    })
  }, [state.summary])

  /** §8.4 자막 삭제 — 확정 자막을 지우고 기록에 tombstone 을 남긴다. */
  const handleDelete = useCallback(
    async (item: FinalItem) => {
      const ok = await confirm({
        title: '이 자막을 삭제할까요?',
        description:
          '접속 중인 참가자 화면에서 즉시 사라지고, 이후 입장자에게도 보이지 않습니다. 기록 중인 방이라면 삭제 사실이 기록 파일에 남습니다(재작성하지 않습니다).',
        confirmLabel: '삭제',
        cancelLabel: '취소',
        variant: 'destructive',
      })
      if (!ok) return

      try {
        await deleteCaption(room, auth.current(), { seq: item.seq, utt: item.utt })
      } catch {
        // 목/실서버 어느 쪽이든 실패는 알린다
        notify('삭제 요청이 실패했습니다.')
        return
      }
      if (USE_MOCK) {
        // 목은 REST 응답만 주므로 팬아웃을 직접 주입한다.
        void import('../mock/client').then((m) => m.mockDelete(room, item.seq, item.utt))
      }
    },
    [room, auth, notify],
  )

  /**
   * 설정 변경 — **기록만 서버 상태**다(§11.3). 나머지(원문 표시·코치 표시)는
   * 이 브라우저의 취향이라 로컬로 끝난다.
   *
   * 서버 왕복이 실패하면 화면을 되돌린다: 켰다고 믿는데 실제로는 안 켜진
   * 상태가 가장 나쁘다(회의가 기록되고 있다고 오해한다).
   */
  const handleSettingsChange = useCallback(
    (next: HostSettings) => {
      const recordChanged = next.record !== settings.record
      // 보존 기간도 **서버 상태**다. 예전에는 이 조건이 record 토글만 보고
      // 있어서, 7/30/90 을 바꿔도 서버 왕복이 아예 일어나지 않았다(선택은
      // 화면에만 남고 방은 계속 기본값). 두 값 중 **무엇이 바뀌든** 보낸다.
      const retentionChanged = next.retentionDays !== settings.retentionDays
      const prevRecord = settings.record
      const prevRetention = settings.retentionDays
      setSettings(next)
      if ((!recordChanged && !retentionChanged) || USE_MOCK) return
      void setRecord(room, auth.current(), next.record, next.retentionDays)
        .then((r) => {
          // 보존 기간의 정본은 **서버**다(§11 클램프) — 응답값으로 맞춘다.
          setSettings((prev) => ({
            ...prev,
            retentionDays: (r.retention_days as 7 | 30 | 90) ?? prev.retentionDays,
          }))
          if (recordChanged) {
            notify(r.record
              ? '자막 기록을 시작했습니다.'
              : '자막 기록을 중지했습니다. 저장된 자막은 보존 기간까지 유지됩니다.')
          } else {
            notify(`보존 기간을 ${r.retention_days}일로 바꿨습니다.`)
          }
        })
        .catch((err: unknown) => {
          // 실패하면 **둘 다** 되돌린다 — 켰다고/바꿨다고 믿는데 실제로는
          // 아닌 상태가 가장 나쁘다(회의가 기록되고 있다고 오해한다).
          setSettings((prev) => ({
            ...prev, record: prevRecord, retentionDays: prevRetention,
          }))
          notify(
            err instanceof ApiError && err.status === 409
              ? '이 서버는 기록을 저장하지 않도록 구성되어 있습니다.'
              : '기록 설정을 바꾸지 못했습니다.',
          )
        })
    },
    [settings.record, settings.retentionDays, room, auth, notify],
  )

  /** §8.5 채팅 삭제 — 자막 삭제(§8.4)와 같은 규율(기록에 tombstone). */
  const handleChatDelete = useCallback(
    async (item: ChatItem) => {
      const ok = await confirm({
        title: '이 메시지를 삭제할까요?',
        description:
          '참가자 화면에서 즉시 사라지고, 이후 입장자에게도 보이지 않습니다. 기록 중인 방이라면 삭제 사실이 기록 파일에 남습니다(재작성하지 않습니다).',
        confirmLabel: '삭제',
        cancelLabel: '취소',
        variant: 'destructive',
      })
      if (!ok) return
      if (USE_MOCK) return
      try {
        await deleteChat(room, auth.current(), item.seq)
      } catch {
        notify('메시지 삭제가 실패했습니다.')
      }
    },
    [room, auth, notify],
  )

  /**
   * 화면 지우기 — 로컬 표시만 비운다(서버 링버퍼는 그대로).
   *
   * 실제 지우는 일은 표시 수위표(clearMark, lib/clear-view.ts)가 한다.
   * 진행 중 발화 자리표는 남는다 — 그 확정 자막은 지우기 **이후**의 새
   * 자막이다. 토스트·confirm 문구는 실제 동작과 일치해야 한다(예전에는
   * confirm 후 토스트만 띄우고 아무것도 지우지 않았다 — B4).
   */
  const clearScreen = useCallback(async () => {
    const ok = await confirm({
      title: '화면의 자막을 지울까요?',
      description:
        '내 화면에서만 사라집니다. 참가자 화면과 서버 기록, 자막 복사·저장·회의록에는 영향을 주지 않습니다. 지운 자막은 새로고침해야 다시 표시됩니다.',
      confirmLabel: '지우기',
      cancelLabel: '취소',
    })
    if (!ok) return
    setClearMark((prev) => markCleared(state.items, prev))
    notify('화면을 지웠습니다. 새 자막부터 다시 표시됩니다(복사·저장·회의록에는 전체가 남습니다).')
  }, [state.items, notify])

  /** §9 명시 종료 — confirm-dialog */
  const closeRoom = useCallback(async () => {
    const ok = await confirm({
      title: '방을 종료할까요?',
      description:
        '모든 참가자의 연결이 끊기고 방이 닫힙니다. 기록하지 않는 방이면 자막은 즉시 소멸합니다. 이 작업은 되돌릴 수 없습니다.',
      confirmLabel: '방 종료',
      cancelLabel: '취소',
      variant: 'destructive',
    })
    if (!ok) return
    await stopMic()
    if (!USE_MOCK) {
      // 서버에서도 닫는다 — 화면만 떠나면 방은 LIVE 로 남아 참가자 연결이
      // 유지되고, 스위퍼가 유휴로 거둘 때까지(§9 기본 2시간) 살아 있다.
      await closeRoomApi(room, auth.current()).catch(() => undefined)
    }
    clearCredential('host')
    void navigate('/')
  }, [stopMic, navigate, room, auth])

  const viewUrl = `${location.origin}/v/${encodeURIComponent(room)}`

  /* ── 헤더 ──────────────────────────────────────────────────────────── */

  /* 휴대폰(≤640px)에서는 머리글을 줄이고 도구줄을 기본 접는다(뷰어와 같은
     패턴, lib/use-header-tools.ts). 단 **마이크 시작/중지·음소거·레벨 미터와
     상태(LIVE·듣는중·지연)는 접혀도 반드시 보인다** — 숨겨진 마이크 버튼은
     회의를 조용히 멈춘다. 넓은 화면은 현행 그대로. */
  const header = (
    <header
      className={`flex flex-wrap items-center border-b border-[var(--color-border)] ${
        tools.narrow ? 'gap-x-1.5 gap-y-1 px-3 py-1.5' : 'gap-2 px-4 py-2.5'
      }`}
    >
      <Brand compact={tools.narrow} />
      {/* S28 회의 이름 — 비어 있으면 그리지 않는다(방 코드 배지가 대신한다).
          휴대폰에서는 도구줄(펼침)에 둔다. */}
      {name && !tools.narrow && <span className="font-medium">{name}</span>}
      <Badge variant="code">{room}</Badge>
      <LiveBadge room={state.room} />
      {/* §14 서버 VAD 기준 발화 상태 — "VI 듣는중" / "통역 중…" */}
      <ListeningBadge live={state.live} />
      <LagBadge room={state.room} />
      {/* 현재 통역 방향 — **항상 보이는 문장**. 화살표만 있는 버튼은 "지금
          어느 방향인지" 를 말해 주지 않고, 방향을 잘못 알면 통역이 통째로
          헛돈다(타깃어 발화는 skip 된다). */}
      <Badge variant="softGray" title="현재 통역 방향">
        {directionLabel(src, dst, { bidirectional: bidi })}
      </Badge>
      <Badge variant="softGray" title="접속 중인 참가자 수">
        {/* 숫자만 따로 감싼다 — e2e(capture.spec.ts)가 인원 변화를 이
            노드의 텍스트로 단언한다. 배지 전체를 잡으면 "참가자 " 라벨까지
            묶여 값 비교가 문구에 묶인다. */}
        참가자 <span data-testid="viewer-count">{state.room.viewers}</span>
      </Badge>

      {/* §8.2 r3 — p50/p95 는 호스트 전용 */}
      <span
        className="flex items-center gap-1.5 text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
        title="발화 종료 → 확정 자막 지연"
      >
        <Signal className="h-3.5 w-3.5" />
        지연 {(state.room.lagMs / 1000).toFixed(1)}s
        {state.room.p50Ms !== null && ` · p50 ${(state.room.p50Ms / 1000).toFixed(1)}s`}
        {state.room.p95Ms !== null && ` · p95 ${(state.room.p95Ms / 1000).toFixed(1)}s`}
      </span>

      {/* 좁은 화면에서는 줄바꿈한다 — 감싸지 않으면 오른쪽 항목들이 화면
          밖으로 밀려 **누를 수 없게** 된다(실측 360px 에서 크기 스테퍼가
          300..460 으로 잘렸다). ms-auto 도 sm 이상에서만 건다: 360px 에서
          오른쪽으로 밀면 줄바꿈이 더 지저분해진다. */}
      <div className="flex flex-wrap items-center gap-2 sm:ms-auto">
        <Button
          variant={micOn ? 'secondary' : 'default'}
          size="sm"
          onClick={() => (micOn ? void stopMic() : void startMic())}
        >
          {micOn ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
          {micOn ? '캡처 중지' : '마이크 시작'}
        </Button>
        {micOn && (
          <>
            {/* §4 수음 레벨 — 입력이 VAD 바닥 아래면 왜 통역이 안 되는지
                이 미터가 즉시 알려 준다. */}
            <LevelMeter
              stats={stats}
              active={micOn && !muted}
              floorDbfs={vadFloorDbfs}
            />
            <Button variant={muted ? 'destructive' : 'outline'} size="sm" onClick={toggleMute}>
              {muted ? '음소거 해제' : '음소거'}
            </Button>
          </>
        )}
        {tools.narrow && (
          <HeaderToolsToggle
            expanded={tools.expanded}
            onToggle={tools.toggle}
            controls="host-header-tools"
          />
        )}
      </div>
      {/* 접히는 도구줄 — 휴대폰에서는 펼쳤을 때만, 머리글 아래 줄에 선다. */}
      {tools.showTools && (
      <div
        id="host-header-tools"
        data-testid="host-header-tools"
        className={`flex flex-wrap items-center gap-2 ${tools.narrow ? 'basis-full' : ''}`}
      >
        {name && tools.narrow && <span className="font-medium">{name}</span>}
        {/* §11.4 회의록 저장 — 범위는 서버가 host_key 로 정한다 */}
        <TranscriptDownload room={room} authKey={auth.current()} recorded={settings.record}
          disabled={roomGone} onNotify={notify} />
        {/* §10.0 관리자에게만 — 회의 중에도 승인 요청을 처리할 수 있게 */}
        <SelfAdminLink />
        <ThemeControls showPresentation />
        <Button variant="destructive" size="sm" onClick={() => void closeRoom()}>
          방 종료
        </Button>
      </div>
      )}
    </header>
  )

  /* ── 우측 어시스트 패널(DEF-071) ──────────────────────────────────────
   *
   * 코치 자동 카드는 **없다**(2026-09-19 사용자 결정: "코치 기능을 빼면 되.
   * 채팅이 코치 역할을 하는거니까"). `CoachPanel` 은 되돌릴 여지로 파일에
   * 남겨 두고 여기서 **호출만 끊었다** — 자동 방출 자체는 서버의
   * `TRANS_COACH=0` 이 막는다(한 줄 되돌리기).
   *
   * 꺼진 서버에는 아예 그리지 않는다(손잡이조차 없다) — 눌러도 아무것도
   * 없는 손잡이는 "뭔가 있는데 안 열린다" 로 읽힌다(§14.2 채팅 버튼과
   * 같은 규율).
   *
   * ⚠️ 판정 축은 **전역 `serverConfig.assist` 하나**다. `room.coach` 에
   * 매달면 그 기본값이 꺼짐이라 기존 방 전부에서 죽은 채 화면만 살아 있다
   * (`api.ts` 의 `CreateRoomInput.coach` 주석에 기록된 사고).
   */
  const rightPanel = serverConfig.assist ? (
    <CoachDock
      collapsed={coachCollapsed}
      onToggle={toggleCoach}
      narrow={coachNarrow}
      itemCount={state.assist.length}
      title="AI 어시스턴트"
    >
      {/* 요약·의도 해석 본문은 여기 두지 않는다(§14.2) — 긴 문서 하나가
          대화 전부를 화면 밖으로 밀어낸다. 전용 드로어로 보내고 여기엔
          **다시 열기** 진입점만 남긴다(기존 "요약" 버튼은 1단계에서
          제거하지 않는다 — 되돌리기 여지). */}
      {summaries.length > 0 && (
        <Button
          size="sm"
          variant="outline"
          className="w-full"
          onClick={() => setSummaryOpen(true)}
          data-testid="summary-reopen"
        >
          {summaries[0] ? kindLabel(summaries[0].kind) : '요약'} 보기
          {summaries.length > 1 && ` (${summaries.length})`}
        </Button>
      )}

      <div className="min-h-0 flex-1">
        <AssistPanel
          items={state.assist}
          pending={assistPending}
          onAsk={askAssist}
        />
      </div>
    </CoachDock>
  ) : undefined

  return (
    <>
      <AppShell header={header} rightPanel={rightPanel}>
        {/* 피드 폭은 창을 따라 넓어진다 — 넓은 화면에서 좁은 칼럼 + 죽은
            여백은 회의실 프로젝터 용례에 특히 손해다. 다만 무한정 넓히면 줄이
            길어져 눈이 줄바꿈을 놓치므로 가독 상한(~1400px)을 둔다. */}
        <div className="mx-auto flex h-full w-full max-w-[min(100%,88rem)] flex-col gap-3">
          <StatusAlerts
            room={state.room}
            conn={conn}
            viewerRole="host"
            lastError={state.lastError}
            credentialDead={auth?.status() === 'credential_dead'}
          />

          {micError && (
            <Alert variant="destructive">
              <MicOff className="h-4 w-4" />
              <AlertDescription>{micError}</AlertDescription>
            </Alert>
          )}

          {/* §4 캡처 생존 — "살아 보이는데 죽어 있는" 상태를 화면이 말한다.
              destructive 가 아니라 경고인 이유: 자동 복구 중이고 대개
              사용자가 할 일이 없다. 다만 **조용히 두지는 않는다**. */}
          {micOn && !micError && micStatusNote && (
            <Alert variant="warning" data-testid="mic-liveness">
              <MicOff className="h-4 w-4" />
              <AlertDescription>{micStatusNote}</AlertDescription>
            </Alert>
          )}

          {/* D4 잡음 바닥 안내 — 고장 안내와 **별도 줄**이다(위 주석의
              이유). warn 이 아니면 아무것도 렌더하지 않는다. */}
          {micOn && !micError && noise.warn && (
            <Alert variant="info" data-testid="noise-floor-note">
              <Mic className="h-4 w-4" />
              <AlertDescription>
                주변 잡음이 큽니다 — 마이크를 가까이 두거나 외부 마이크를 써 보세요
              </AlertDescription>
            </Alert>
          )}

          {!micOn && !micError && (
            <Alert variant="info">
              <Mic className="h-4 w-4" />
              <AlertDescription>
                마이크를 시작하면 발화가 통역되어 참가자 화면에 표시됩니다. 오디오는 저장되지
                않습니다.
              </AlertDescription>
            </Alert>
          )}

          {/* §14 button-group — 방향·복사·저장·요약·지우기·기록·의도해석·설정.
              휴대폰(≤640px)에서는 머리글의 [도구▾] 와 같은 토글로 접힌다
              (기본 접힘, lib/use-header-tools.ts) — 실측 390px 에서 5줄을
              먹었다. 지금 방향은 머리글의 방향 배지가 접혀도 말한다. */}
          {tools.showTools && (
          <ButtonGroup className="flex-wrap" aria-disabled={roomGone}
            data-testid="host-action-bar">
            {/* §bidi 통역 방향 3-상태 세그먼트 — vi→ko / ko→vi / 양방향.
                화살표 하나짜리 스왑 버튼은 세 번째 상태(양방향)를 담을 수 없어
                세 상태를 나란히 고르는 컨트롤로 바꿨다. 지금 상태는
                aria-pressed·표면(secondary)으로 말한다. */}
            <div
              role="group"
              aria-label="통역 방향"
              data-testid="direction-control"
              className="inline-flex overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-border)]"
            >
              {directionOptions.map((opt) => {
                const active = opt.bidi
                  ? bidi
                  : !bidi && src === opt.src && dst === opt.dst
                return (
                  <Button
                    key={opt.key}
                    variant={active ? 'secondary' : 'ghost'}
                    size="sm"
                    aria-pressed={active}
                    data-testid={`direction-${opt.key}`}
                    disabled={roomGone || opt.disabled}
                    title={opt.tooltip
                      ? opt.tooltip
                      : opt.disabled
                      ? '같은 언어끼리는 통역이 없습니다'
                      : `통역 방향: ${opt.label}`}
                    className="rounded-none border-0"
                    onClick={() => applyDirection(opt.src, opt.dst, opt.bidi)}
                  >
                    {opt.bidi ? (
                      <ArrowLeftRight className="h-4 w-4" />
                    ) : null}
                    {opt.bidi
                      ? '양방향'
                      : directionLabel(opt.src, opt.dst, { compact: true })}
                  </Button>
                )
              })}
            </div>
            {/* 참가자에게 보낼 초대 문구 — 패스코드는 링크가 아니라
                **평문**으로 실린다(§10.0.1). */}
            <Button
              variant={entryCopied ? 'secondary' : 'outline'}
              size="sm"
              onClick={copyEntryInfo}
              disabled={roomGone}
              title={
                publicEntry
                  ? '참가자에게 보낼 직통 링크를 복사합니다'
                  : '참가자에게 보낼 주소와 입장 패스코드를 복사합니다'
              }
            >
              {entryCopied ? <Check className="h-4 w-4" /> : <Share2 className="h-4 w-4" />}
              {entryCopied ? '복사됨' : '입장 정보 복사'}
            </Button>
            <Button variant="outline" size="sm" onClick={copyAll}
              disabled={roomGone} title="전체 자막 복사">
              <Copy className="h-4 w-4" />
              자막 복사
            </Button>
            <Button variant="outline" size="sm" onClick={saveAll}
              disabled={roomGone} title="텍스트 파일로 저장">
              <Save className="h-4 w-4" />
              저장
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => requestSummary('summary')}
              disabled={roomGone}
              title="지금까지의 회의 요약"
              // 어시스트 빠른 버튼("지금까지 요약")과 접근성 이름이 겹친다 —
              // 헤더의 이 버튼을 가리키는 안정된 축을 둔다(1단계에서는 둘 다
              // 남긴다: 되돌리기 여지).
              data-testid="summary-run"
            >
              <FileText className="h-4 w-4" />
              요약
            </Button>
            <Button variant="outline" size="sm" onClick={() => void clearScreen()}
              disabled={roomGone} title="내 화면만 지우기">
              <Eraser className="h-4 w-4" />
              지우기
            </Button>
            {/* 원문 가림 — 로컬 표시 취향이라 roomGone 에도 살아 있다.
                버튼은 **누르면 무엇이 되는지** 적고(§14.1 규율), 지금 상태는
                aria-pressed·표면(variant)으로 말한다 — 가려 둔 것을 잊고
                "원문이 안 잡히나" 로 오해하지 않게. */}
            <Button
              variant={veil.veiled ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={veil.veiled}
              data-testid="veil-toggle"
              onClick={veil.toggle}
              title={
                veil.veiled
                  ? '가려 둔 원문(들린 내용)을 다시 표시합니다'
                  : '원문(들린 내용)을 화면에서만 가립니다 — 복사·회의록에는 남습니다'
              }
            >
              {veil.veiled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              {/* 라벨 스왑으로 버튼 폭이 변하면 좁은 화면에서 이웃 버튼이 밀린다
                  (QA P3 실측 12px@360px) — 두 라벨을 같은 칸에 겹쳐 폭을 항상
                  긴 쪽으로 고정한다. invisible(visibility:hidden)은 접근성
                  트리에서도 빠져 스크린리더는 보이는 라벨만 읽는다. */}
              <span className="grid text-start">
                <span className={`col-start-1 row-start-1 ${veil.veiled ? 'invisible' : ''}`}>
                  원문 가리기
                </span>
                <span className={`col-start-1 row-start-1 ${veil.veiled ? '' : 'invisible'}`}>
                  원문 표시
                </span>
              </span>
            </Button>
            {/* 추론(유령) 자막 접기 — 원문 가림과 나란히, 같은 라벨 스왑 규율. */}
            <Button
              variant={collapseInferred.collapsed ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={collapseInferred.collapsed}
              data-testid="collapse-inferred-toggle"
              onClick={collapseInferred.toggle}
              title={
                collapseInferred.collapsed
                  ? '지어낸 것으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다 — 지금은 접힌 상태입니다.'
                  : '지어낸 것으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다.'
              }
            >
              {collapseInferred.collapsed ? (
                <ChevronsUpDown className="h-4 w-4" />
              ) : (
                <ChevronsDownUp className="h-4 w-4" />
              )}
              <span className="grid text-start">
                <span
                  className={`col-start-1 row-start-1 ${collapseInferred.collapsed ? 'invisible' : ''}`}
                >
                  추론 접기
                </span>
                <span
                  className={`col-start-1 row-start-1 ${collapseInferred.collapsed ? '' : 'invisible'}`}
                >
                  추론 펼치기
                </span>
              </span>
            </Button>
            {/* 판정기 자막 접기 — 추론 접기와 나란히, 같은 라벨 스왑 규율.
                **배지가 실제로 도는 방에서만** 손잡이가 선다(T3): 배지 없이
                접히면 "이유 없이 사라진 줄" 이 되고, 판정기가 꺼진 방에 손잡이만
                서면 그것이 거짓 손잡이다(`main.py:858` 과 같은 규율). */}
            {judgeBadgeLive && (
              <Button
                variant={collapseAssistant.collapsed ? 'secondary' : 'outline'}
                size="sm"
                aria-pressed={collapseAssistant.collapsed}
                data-testid="collapse-assistant-toggle"
                onClick={collapseAssistant.toggle}
                title={
                  collapseAssistant.collapsed
                    ? '통역기 답변으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다 — 지금은 접힌 상태입니다.'
                    : '통역기 답변으로 보이는 자막을 한 줄로 접습니다. 데이터는 지워지지 않습니다.'
                }
              >
                {collapseAssistant.collapsed ? (
                  <ChevronsUpDown className="h-4 w-4" />
                ) : (
                  <ChevronsDownUp className="h-4 w-4" />
                )}
                <span className="grid text-start">
                  <span
                    className={`col-start-1 row-start-1 ${collapseAssistant.collapsed ? 'invisible' : ''}`}
                  >
                    답변 접기
                  </span>
                  <span
                    className={`col-start-1 row-start-1 ${collapseAssistant.collapsed ? '' : 'invisible'}`}
                  >
                    답변 펼치기
                  </span>
                </span>
              </Button>
            )}
            {/* 기록은 **개설 시점 확정**이다(§11) — 여기서 끌 수 없다.
                토글처럼 보이게 두면 껐다고 믿은 호스트가 계속 기록되는
                상황이 생긴다. 현재 상태만 배지로 알린다. */}
            <Badge
              variant={settings.record ? 'softSuccess' : 'softGray'}
              className="gap-1 self-center"
              title={
                settings.record
                  ? `자막을 기록 중입니다(보존 ${settings.retentionDays}일). 설정에서 끌 수 있습니다.`
                  : '기록하지 않는 방입니다. 설정에서 켤 수 있습니다.'
              }
            >
              <Radio className="h-3 w-3" />
              {settings.record ? `기록 중 · ${settings.retentionDays}일` : '기록 안 함'}
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => requestSummary('intent')}
              disabled={roomGone}
              title="상대 발언의 의도 해석"
            >
              <Brain className="h-4 w-4" />
              의도 해석
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}
              disabled={roomGone}>
              <Settings className="h-4 w-4" />
              설정
            </Button>
          </ButtonGroup>
          )}

          <div className="min-h-0 flex-1">
            {/* 3분기(SPLITVIEW_ROWS_DESIGN D3-1·D3-4) — `rows` 는 데스크톱
                에서만 행 정렬이고, ≤640px 에서는 v4 탭 계약으로 폴백한다. */}
            {splitMode === 'rows' && !splitRowsFallback ? (
              <SplitRows
                items={feedItems}
                src={src}
                dst={dst}
                veilSource={veil.veiled}
                collapseInferred={collapseInferred.collapsed}
                collapseAssistant={collapseAssistant.collapsed}
                onDelete={(item) => void handleDelete(item)}
                /* 지운 뒤에는 과거 스크롤 로드를 멈춘다 — clearMark 규칙은
                   행 정렬에도 그대로 적용된다(host.tsx 통합 경로와 같은 의미). */
                {...(clearMark ? {} : { onLoadMore: loadMore })}
                presentation={presentation}
                order={order}
                {...(clearMark
                  ? {
                      emptyTitle: '화면을 지웠습니다',
                      emptyDescription:
                        '새 자막부터 다시 표시됩니다. 지운 자막은 복사·저장·회의록에 남아 있고, 새로고침하면 다시 보입니다.',
                    }
                  : {})}
              />
            ) : splitMode !== 'unified' ? (
              <SplitFeed
                items={feedItems}
                src={src}
                dst={dst}
                veilSource={veil.veiled}
                collapseInferred={collapseInferred.collapsed}
                collapseAssistant={collapseAssistant.collapsed}
                onDelete={(item) => void handleDelete(item)}
                /* 지운 뒤에는 과거 스크롤 로드를 멈춘다 — clearMark 규칙은
                   공유 로더 수준까지 그대로 적용된다(D2-6.4, host.tsx:1067
                   의 의미 보존). */
                {...(clearMark ? {} : { onLoadMore: loadMore })}
                presentation={presentation}
                order={order}
                conn={conn}
                {...(clearMark
                  ? {
                      liveEmptyTitle: '화면을 지웠습니다',
                      liveEmptyDescription:
                        '새 자막부터 다시 표시됩니다. 지운 자막은 복사·저장·회의록에 남아 있고, 새로고침하면 다시 보입니다.',
                    }
                  : {})}
              />
            ) : (
              <CaptionFeed
                items={feedItems}
                src={src}
                dst={dst}
                veilSource={veil.veiled}
                collapseInferred={collapseInferred.collapsed}
                collapseAssistant={collapseAssistant.collapsed}
                onDelete={(item) => void handleDelete(item)}
                /* 지운 뒤에는 과거 스크롤 로드를 멈춘다 — 히스토리는 지운
                   범위보다 오래된 것만 가져오므로, 허용하면 지운 구간이
                   조용한 구멍이 된다(lib/clear-view.ts). 새로고침이 복원 경로다. */
                {...(clearMark ? {} : { onLoadMore: loadMore })}
                presentation={presentation}
                order={order}
                conn={conn}
                {...(clearMark
                  ? {
                      emptyTitle: '화면을 지웠습니다',
                      emptyDescription:
                        '새 자막부터 다시 표시됩니다. 지운 자막은 복사·저장·회의록에 남아 있고, 새로고침하면 다시 보입니다.',
                    }
                  : {})}
              />
            )}
          </div>
        </div>
      </AppShell>

      <SettingsDrawer
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onChange={handleSettingsChange}
        step={scale.step}
        onStepChange={scale.setStep}
        order={order}
        onOrderChange={setOrder}
        splitMode={splitMode}
        onSplitModeChange={setSplitMode}
        veiled={veil.veiled}
        onVeiledChange={veil.setVeiled}
        wakeLock={wakeLockPref.enabled}
        onWakeLockChange={wakeLockPref.setEnabled}
        wakeLockStatus={wakeLockStatus}
        stats={stats}
        linkStats={linkStats}
        room={room}
        passcode={passcode}
        direction={directionLabel(src, dst, { bidirectional: bidi })}
        viewUrl={viewUrl}
      />

      {/* §14.2 요약·의도 해석 — 코치 피드와 분리된 자기 표면 */}
      <SummaryDrawer
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
        entries={summaries}
        selectedId={summarySel}
        onSelect={setSummarySel}
        pending={summaryPending}
      />

      {/* §8.5 회의 중 채팅 — 서버가 켠 경우에만(§14.2) */}
      {serverConfig.chat && (
        <ChatPanel
          chat={state.chat}
          onSend={sendChat}
          onDelete={(item) => void handleChatDelete(item)}
          error={state.chatError}
          disabled={roomGone}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-[var(--radius-lg)] bg-[var(--color-foreground)] px-4 py-2 text-(length:--text-sm-size) text-[var(--color-background)] shadow-lg"
        >
          {toast}
        </div>
      )}
    </>
  )
}
